import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Mail, Loader2 } from "lucide-react";
import type { EmailTemplate } from "@shared/schema";

const sendEmailSchema = z.object({
  to: z.string().email("Invalid email address"),
  subject: z.string().min(1, "Subject is required"),
  body: z.string().min(1, "Email body is required"),
});

type SendEmailForm = z.infer<typeof sendEmailSchema>;

interface EmailComposerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipientEmail?: string;
  recipientName?: string;
  contactId?: string;
  dealId?: string;
}

export function EmailComposerDialog({
  open,
  onOpenChange,
  recipientEmail = "",
  recipientName = "",
  contactId,
  dealId,
}: EmailComposerDialogProps) {
  const { toast } = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");

  const form = useForm<SendEmailForm>({
    resolver: zodResolver(sendEmailSchema),
    defaultValues: {
      to: recipientEmail,
      subject: "",
      body: "",
    },
  });

  // Fetch email templates
  const { data: templates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/email-templates"],
    enabled: open,
  });

  // Send email mutation
  const sendEmail = useMutation({
    mutationFn: async (data: SendEmailForm & { contactId?: string; dealId?: string }) => {
      return apiRequest("POST", "/api/emails/send", data);
    },
    onSuccess: () => {
      toast({
        title: "Email sent successfully",
        description: "The email has been sent and logged as an activity.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/activities"] });
      form.reset();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send email",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: SendEmailForm) => {
    sendEmail.mutate({
      ...data,
      contactId,
      dealId,
    });
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      // Replace placeholders with actual values
      const personalizedSubject = template.subject
        .replace(/{{firstName}}/g, recipientName.split(" ")[0] || recipientName)
        .replace(/{{company}}/g, "your company")
        .replace(/{{senderName}}/g, "Your Name");

      const personalizedBody = template.body
        .replace(/{{firstName}}/g, recipientName.split(" ")[0] || recipientName)
        .replace(/{{company}}/g, "your company")
        .replace(/{{senderName}}/g, "Your Name");

      form.setValue("subject", personalizedSubject);
      form.setValue("body", personalizedBody);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Compose Email
          </DialogTitle>
          <DialogDescription>
            Send an email to {recipientName || recipientEmail}. The email will be automatically logged as an activity.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="to"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>To</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" placeholder="recipient@example.com" data-testid="input-email-to" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <FormLabel>Template (Optional)</FormLabel>
              <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                <SelectTrigger data-testid="select-email-template">
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id} data-testid={`template-${template.id}`}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Email subject" data-testid="input-email-subject" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Write your email message here..."
                      rows={10}
                      data-testid="textarea-email-body"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={sendEmail.isPending}
                data-testid="button-cancel-email"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={sendEmail.isPending} data-testid="button-send-email">
                {sendEmail.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default EmailComposerDialog;
